import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { DataGrid, type GridColDef, type GridRowParams } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { errorMessage } from '@/api/common';
import { mediaNetworkApi } from '@/api/media-network/mediaNetworkApi';
import { mediaNetworkKeys } from '@/api/media-network/mediaNetworkQueries';
import { renditionForProfile, type Creative } from '@/models/media-network/Creative';
import { useAppContext } from '../../AppContext';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';
import { usePermissions } from '../../permissions/usePermissions';
import { Perm } from '../../permissions/permissions';
import { fillHeight } from '../../theme';
import DataGridDeleteButton from '../common/data-grid/DataGridDeleteButton';
import DataGridEditButton from '../common/data-grid/DataGridEditButton';
import { useToast } from '../common/toast/ToastProvider';
import CreativeDialog from './dialogs/CreativeDialog';

// The shared media library.
//
// Its own nav item rather than something reached through a playlist, because a creative belongs to the brand
// and not to any one feature: Ad Banner and Cross-Sell draw from this same shelf, and a creative exists
// whether or not anything currently uses it. Uploading artwork is also a different job from scheduling it,
// often a different person, which is why it carries its own permission.
const MediaLibrary = () => {
    const qc = useQueryClient();
    const toast = useToast();
    const strings = useLocalizedStrings();
    const app = useAppContext();
    const perms = usePermissions();
    const canCreate = perms.canCreate(Perm.mediaNetworkMediaLibrary);
    const canUpdate = perms.canUpdate(Perm.mediaNetworkMediaLibrary);
    const canDelete = perms.canDelete(Perm.mediaNetworkMediaLibrary);

    const brandId = app.brandId ?? undefined;

    const [editing, setEditing] = useState<string | null>(null);
    // The creative "New" just made. Tracked so cancelling can throw it away — see the create mutation.
    const [created, setCreated] = useState<string | null>(null);

    const { data: creatives, isPending } = useQuery({
        queryKey: mediaNetworkKeys.creatives(brandId ?? ''),
        enabled: !!brandId,
        queryFn: () => mediaNetworkApi.listCreatives(brandId!),
    });

    const { data: profiles = [] } = useQuery({
        queryKey: mediaNetworkKeys.displayProfiles(),
        queryFn: () => mediaNetworkApi.listDisplayProfiles(),
        staleTime: Infinity,
    });

    // A creative starts empty and gets its files in the dialog. Two steps rather than "pick a file and we'll
    // name it after that": a creative is several files, so there is no one filename to borrow.
    //
    // The row is created UP FRONT because an upload needs a creative to belong to — the blob key is built from
    // its id, and the bytes go straight to storage. That is an implementation need, not something the user
    // asked for, so it is hidden: the list is NOT refreshed here, and cancelling deletes the row again. Until
    // the dialog is saved, nothing about it is visible.
    const { mutateAsync: create, isPending: isCreating } = useMutation({
        mutationFn: () => mediaNetworkApi.createCreative({ brandId: brandId!, name: strings.creativeUntitled }),
        onSuccess: (result) => {
            setCreated(result.id);
            setEditing(result.id);
        },
        onError: () => toast({ severity: 'error', message: strings.creativeSaveError }),
    });

    const { mutateAsync: remove } = useMutation({
        mutationFn: (id: string) => mediaNetworkApi.deleteCreative(id),
        onSuccess: async () => {
            toast({ severity: 'success', message: strings.creativeDeleted });
            await qc.invalidateQueries({ queryKey: mediaNetworkKeys.creatives(brandId ?? '') });
        },
        // Refused while a playlist still uses it, and the message names them. Surface it rather than a
        // generic failure, because it says exactly what to go and do.
        onError: (e: unknown) => toast({
            severity: 'error',
            message: errorMessage(e, strings.creativeDeleteError),
        }),
    });

    const columns: GridColDef<Creative>[] = [
        { field: 'name', headerName: strings.name, flex: 2 },
        {
            // One chip per shape, filled or not. The whole point of the library view: "what have I got, and
            // what is still missing" answered without opening anything.
            field: 'coverage', headerName: strings.renditions, flex: 2, sortable: false,
            renderCell: (params) => (
                <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', height: '100%' }}>
                    {profiles.map((p) => {
                        const has = !!renditionForProfile(params.row, p);
                        return (
                            <Tooltip key={p.id} title={`${p.name} (${p.display})`}>
                                <Chip
                                    size="small"
                                    variant={has ? 'filled' : 'outlined'}
                                    color={has ? 'success' : 'default'}
                                    icon={has ? <CheckCircleIcon /> : undefined}
                                    label={p.display}
                                />
                            </Tooltip>
                        );
                    })}
                </Stack>
            ),
        },
        {
            field: 'usageCount', headerName: strings.creativeUsage, width: 100,
            renderCell: (params) => (
                <Stack direction="row" sx={{ alignItems: 'center', height: '100%' }}>
                    <Typography variant="body2">{params.row.usageCount}</Typography>
                </Stack>
            ),
        },
        {
            field: 'actions', headerName: strings.actions, type: 'actions', width: 110,
            getActions: (params: GridRowParams<Creative>) => [
                <DataGridEditButton
                    id={params.id.toString()}
                    onClick={() => setEditing(params.row.id)}
                    aria-label="edit creative"
                    readOnly={!canUpdate}
                />,
                <DataGridDeleteButton
                    id={params.id.toString()}
                    getWarningMessage={(id) => strings.creativeDeleteConfirm
                        .replace('{{name}}', creatives?.find((c) => c.id === id)?.name ?? '')}
                    onClick={(id) => remove(id)}
                    aria-label="delete creative"
                    hidden={!canDelete}
                />,
            ],
        },
    ];

    if (!brandId) {
        return (
            <Box sx={{ ...fillHeight, p: 2 }}>
                <Typography variant="body2" color="text.secondary">{strings.playlistSelectBrandTopBar}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ ...fillHeight, overflow: 'hidden', p: 2 }}>
            <Stack spacing={1} sx={{ height: '100%', minHeight: 0 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6">{strings.mediaLibrary}</Typography>
                    {canCreate && (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => create()}
                            loading={isCreating}
                            size="input"
                        >
                            {strings.creativeNew}
                        </Button>
                    )}
                </Stack>

                <Box sx={{ flex: 1, minHeight: 300 }}>
                    <DataGrid
                        rows={creatives ?? []}
                        columns={columns}
                        loading={isPending}
                        getRowId={(row) => row.id}
                        disableRowSelectionOnClick
                        disableColumnMenu
                        localeText={{ noRowsLabel: strings.creativeNone }}
                    />
                </Box>
            </Stack>

            <CreativeDialog
                open={!!editing}
                brandId={brandId}
                creativeId={editing}
                isNew={!!editing && editing === created}
                canUpdate={canUpdate}
                onClose={() => { setEditing(null); setCreated(null); }}
            />
        </Box>
    );
};

export default MediaLibrary;
